import {
  AuditAction,
  AuditResource,
  AuditVisibility,
  AuditSeverity,
  AuditSource,
} from '@hapana/shared/schemas/audit'

/**
 * Format action name from SNAKE_CASE to Title Case
 * Example: DOOR_ACCESS_GRANTED -> Door Access Granted
 */
export function formatActionName(action: string): string {
  if (!action) return ''
  return action
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export const DEFAULT_PAGE_SIZE = 25
export const PAGE_SIZE_OPTIONS = [
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
]

export const DEFAULT_FILTERS = {
  actions: [] as AuditAction[],
  resources: [] as AuditResource[],
  visibility: [] as AuditVisibility[],
  severity: [] as AuditSeverity[],
  source: [] as AuditSource[],
  searchText: '',
  dateRange: undefined,
}

export const DEFAULT_SORT = {
  field: 'timestamp' as const,
  direction: 'desc' as const,
}

/** Map action types to human-readable labels */
export const ACTION_LABELS: Record<AuditAction, string> = {
  // Member Events
  [AuditAction.MEMBER_CREATED]: 'Member Created',
  [AuditAction.MEMBER_UPDATED]: 'Member Updated',
  [AuditAction.MEMBER_DELETED]: 'Member Deleted',
  [AuditAction.MEMBER_LOGIN]: 'Member Login',
  [AuditAction.MEMBER_LOGOUT]: 'Member Logout',
  [AuditAction.MEMBER_CHECKIN]: 'Member Check-in',
  [AuditAction.MEMBER_CHECKOUT]: 'Member Check-out',
  [AuditAction.MEMBER_PROFILE_VIEWED]: 'Member Profile Viewed',

  // Session/Booking Events
  [AuditAction.SESSION_BOOKED]: 'Session Booked',
  [AuditAction.SESSION_CANCELLED]: 'Session Cancelled',
  [AuditAction.SESSION_ATTENDED]: 'Session Attended',
  [AuditAction.SESSION_NO_SHOW]: 'Session No Show',
  [AuditAction.SESSION_WAITLISTED]: 'Session Waitlisted',
  [AuditAction.SESSION_CREATED]: 'Session Created',
  [AuditAction.SESSION_UPDATED]: 'Session Updated',
  [AuditAction.SESSION_DELETED]: 'Session Deleted',

  // Payment Events
  [AuditAction.PAYMENT_PROCESSED]: 'Payment Processed',
  [AuditAction.PAYMENT_FAILED]: 'Payment Failed',
  [AuditAction.PAYMENT_REFUNDED]: 'Payment Refunded',
  [AuditAction.PAYMENT_GATEWAY_FEES_APPLIED]: 'Payment Gateway Fees Applied',
  [AuditAction.PAYMENT_METHOD_ADDED]: 'Payment Method Added',
  [AuditAction.PAYMENT_METHOD_REMOVED]: 'Payment Method Removed',
  // CORE-1322 / INT-67: neutral enum values (DD-011), user-facing labels say "Ezypay Account Code …".
  [AuditAction.ACCOUNT_CODE_CREATED]: 'Ezypay Account Code Created',
  [AuditAction.ACCOUNT_CODE_UPDATED]: 'Ezypay Account Code Updated',
  [AuditAction.ACCOUNT_CODE_ACTIVATED]: 'Ezypay Account Code Activated',
  [AuditAction.ACCOUNT_CODE_DEACTIVATED]: 'Ezypay Account Code Deactivated',
  [AuditAction.ACCOUNT_CODE_ASSIGNED]: 'Ezypay Account Code Assigned',
  [AuditAction.ACCOUNT_CODE_UNASSIGNED]: 'Ezypay Account Code Unassigned',
  [AuditAction.INVOICE_CREATED]: 'Invoice Created',
  [AuditAction.INVOICE_PAID]: 'Invoice Paid',
  [AuditAction.INVOICE_VOIDED]: 'Invoice Voided',

  // Membership Events
  [AuditAction.MEMBERSHIP_CREATED]: 'Membership Created',
  [AuditAction.MEMBERSHIP_UPDATED]: 'Membership Updated',
  [AuditAction.MEMBERSHIP_CANCELLED]: 'Membership Cancelled',
  [AuditAction.MEMBERSHIP_SUSPENDED]: 'Membership Suspended',
  [AuditAction.MEMBERSHIP_RESUMED]: 'Membership Resumed',
  [AuditAction.MEMBERSHIP_RENEWED]: 'Membership Renewed',

  // Staff Events
  [AuditAction.STAFF_LOGIN]: 'Staff Login',
  [AuditAction.STAFF_LOGOUT]: 'Staff Logout',
  [AuditAction.STAFF_CREATED]: 'Staff Created',
  [AuditAction.STAFF_UPDATED]: 'Staff Updated',
  [AuditAction.STAFF_DELETED]: 'Staff Deleted',
  [AuditAction.STAFF_ROLE_CHANGED]: 'Staff Role Changed',

  // Admin Events
  [AuditAction.ADMIN_USER_LOGIN]: 'Admin Login',
  [AuditAction.ADMIN_USER_LOGOUT]: 'Admin Logout',
  [AuditAction.ADMIN_USER_CREATED]: 'Admin Created',
  [AuditAction.ADMIN_USER_UPDATED]: 'Admin Updated',
  [AuditAction.ADMIN_USER_DELETED]: 'Admin Deleted',

  // Corporate/Site Events
  [AuditAction.CORPORATE_CREATED]: 'Corporate Created',
  [AuditAction.CORPORATE_UPDATED]: 'Corporate Updated',
  [AuditAction.SITE_CREATED]: 'Site Created',
  [AuditAction.SITE_UPDATED]: 'Site Updated',
  [AuditAction.SITE_DELETED]: 'Site Deleted',
  [AuditAction.SITE_V2_STATUS_CHANGED]: 'Site V2 Status Changed',

  // Door/Access Events
  [AuditAction.DOOR_ACCESS_GRANTED]: 'Door Access Granted',
  [AuditAction.DOOR_ACCESS_DENIED]: 'Door Access Denied',
  [AuditAction.DOOR_UNLOCKED]: 'Door Unlocked',
  [AuditAction.DOOR_UNLOCK_FAILED]: 'Door Unlock Failed',
  [AuditAction.DOOR_LOCKED]: 'Door Locked',
  [AuditAction.ACCESS_POINT_MOVED]: 'Access Point Moved',
  [AuditAction.ACCESS_POINT_DELETED]: 'Access Point Deleted',
  [AuditAction.ACCESS_POINT_CREATED]: 'Access Point Created',
  [AuditAction.ACCESS_POINT_UPDATED]: 'Access Point Updated',
  [AuditAction.ACCESS_POINT_QR_GENERATED]: 'Access Point QR Generated',
  [AuditAction.ACCESS_ZONE_CREATED]: 'Access Zone Created',
  [AuditAction.ACCESS_ZONE_UPDATED]: 'Access Zone Updated',
  [AuditAction.ACCESS_ZONE_DELETED]: 'Access Zone Deleted',

  // System Events
  [AuditAction.SYSTEM_CONFIG_CHANGED]: 'System Config Changed',
  [AuditAction.DATA_EXPORT]: 'Data Export',
  [AuditAction.DATA_IMPORT]: 'Data Import',
  [AuditAction.GDPR_REQUEST]: 'GDPR Request',
  [AuditAction.DATA_DELETION]: 'Data Deletion',

  // Settings Events
  [AuditAction.SETTING_UPDATED]: 'Setting Updated',
  [AuditAction.SITE_SETTING_UPDATED]: 'Site Setting Updated',
  [AuditAction.BRAND_SETTING_UPDATED]: 'Brand Setting Updated',

  // Page/Navigation Events
  [AuditAction.PAGE_VIEWED]: 'Page Viewed',
  [AuditAction.FEATURE_ACCESSED]: 'Feature Accessed',

  // Knowledge Hub Events
  [AuditAction.KNOWLEDGE_ARTICLE_CREATED]: 'Knowledge Article Created',
  [AuditAction.KNOWLEDGE_ARTICLE_UPDATED]: 'Knowledge Article Updated',
  [AuditAction.KNOWLEDGE_ARTICLE_PUBLISHED]: 'Knowledge Article Published',
  [AuditAction.KNOWLEDGE_ARTICLE_ARCHIVED]: 'Knowledge Article Archived',
  [AuditAction.KNOWLEDGE_ARTICLE_DELETED]: 'Knowledge Article Deleted',
  [AuditAction.KNOWLEDGE_ARTICLE_RESTORED]: 'Knowledge Article Restored',
  [AuditAction.KNOWLEDGE_ARTICLE_TRANSLATION_ADDED]: 'Knowledge Article Translation Added',
  [AuditAction.KNOWLEDGE_CATEGORY_CREATED]: 'Knowledge Category Created',
  [AuditAction.KNOWLEDGE_CATEGORY_UPDATED]: 'Knowledge Category Updated',
  [AuditAction.KNOWLEDGE_CATEGORY_DELETED]: 'Knowledge Category Deleted',
  [AuditAction.KNOWLEDGE_CATEGORY_REORDERED]: 'Knowledge Category Reordered',
  [AuditAction.KNOWLEDGE_ARTICLE_VIDEO_UPLOADED]: 'Knowledge Article Video Uploaded',
  [AuditAction.KNOWLEDGE_ARTICLE_IMAGE_UPLOADED]: 'Knowledge Article Image Uploaded',

  // AI Assistant Events
  [AuditAction.AI_ARTICLE_GENERATED]: 'AI Article Generated',
  [AuditAction.AI_CONVERSATION_STARTED]: 'AI Conversation Started',
  [AuditAction.AI_CONVERSATION_ENDED]: 'AI Conversation Ended',
  [AuditAction.AI_INTENT_DETECTED]: 'AI Intent Detected',
  [AuditAction.AI_RESPONSE_GENERATED]: 'AI Response Generated',
  [AuditAction.AI_FEEDBACK_PROVIDED]: 'AI Feedback Provided',
  [AuditAction.AI_SUPPORT_TICKET_CREATED]: 'AI Support Ticket Created',
  [AuditAction.AI_KNOWLEDGE_SEARCH]: 'AI Knowledge Search',

  // Additional backend events
  [AuditAction.INVOICE_UPDATED]: 'Invoice Updated',
  [AuditAction.MEMBER_MERGED]: 'Member Merged',
  [AuditAction.IMPERSONATION_STARTED]: 'Impersonation Started',
  [AuditAction.IMPERSONATION_ENDED]: 'Impersonation Ended',

  // Communication events
  [AuditAction.EMAIL_SENT]: 'Email Sent',
  [AuditAction.EMAIL_DELIVERED]: 'Email Delivered',
  [AuditAction.EMAIL_OPENED]: 'Email Opened',
  [AuditAction.EMAIL_BOUNCED]: 'Email Bounced',
  [AuditAction.EMAIL_COMPLAINED]: 'Email Spam Complaint',
  [AuditAction.EMAIL_CLICKED]: 'Email Link Clicked',
  [AuditAction.EMAIL_FAILED]: 'Email Failed',
  [AuditAction.EMAIL_SUPPRESSED]: 'Email Suppressed',
  [AuditAction.SMS_SENT]: 'SMS Sent',
  [AuditAction.SMS_DELIVERED]: 'SMS Delivered',
  [AuditAction.PUSH_NOTIFICATION_SENT]: 'Push Notification Sent',
  [AuditAction.PUSH_NOTIFICATION_DELIVERED]: 'Push Notification Delivered',

  // Notes events
  [AuditAction.NOTE_CREATED]: 'Note Created',
  [AuditAction.NOTE_UPDATED]: 'Note Updated',

  // Ecommerce events
  [AuditAction.ECOMMERCE_PURCHASE]: 'Ecommerce Purchase',

  // Integration Sync Events
  [AuditAction.INTEGRATION_SYNC_STARTED]: 'Integration Sync Started',
  [AuditAction.INTEGRATION_SYNC_COMPLETED]: 'Integration Sync Completed',
  [AuditAction.INTEGRATION_SYNC_FAILED]: 'Integration Sync Failed',
  [AuditAction.INTEGRATION_CLIENT_SYNCED]: 'Integration Client Synced',
  [AuditAction.INTEGRATION_CLIENT_SYNC_FAILED]: 'Integration Client Sync Failed',
  [AuditAction.INTEGRATION_TOKEN_REFRESHED]: 'Integration Token Refreshed',
  [AuditAction.INTEGRATION_TOKEN_REFRESH_FAILED]: 'Integration Token Refresh Failed',

  // Grow Corporate Settings Events
  [AuditAction.GROW_CORPORATE_SETTINGS_UPDATED]: 'Grow Settings Updated',

  // Grow HQ Events
  [AuditAction.GROW_HQ_CREATED]: 'Grow HQ Created',
  [AuditAction.GROW_HQ_UPDATED]: 'Grow HQ Updated',
  [AuditAction.GROW_HQ_DELETED]: 'Grow HQ Deleted',
  [AuditAction.GROW_HQ_AUTHENTICATION_SET]: 'Grow HQ Authentication Set',
  [AuditAction.GROW_HQ_AUTHENTICATION_CLEARED]: 'Grow HQ Authentication Cleared',
  [AuditAction.GROW_HQ_SYNC_TRIGGERED]: 'Grow HQ Sync Triggered',

  // Grow Site Events
  [AuditAction.GROW_SITE_AUTHENTICATION_SET]: 'Grow Site Authentication Set',
  [AuditAction.GROW_SITE_AUTHENTICATION_CLEARED]: 'Grow Site Authentication Cleared',
  [AuditAction.GROW_SITE_LINKED_TO_HQ]: 'Grow Site Linked to HQ',
  [AuditAction.GROW_SITE_UNLINKED_FROM_HQ]: 'Grow Site Unlinked from HQ',
  [AuditAction.GROW_SITE_LOCATION_CREATED]: 'Grow Site Location Created',
  [AuditAction.GROW_SITE_SYNC_TRIGGERED]: 'Grow Site Sync Triggered',

  // Grow Custom Field Events
  [AuditAction.GROW_CUSTOM_FIELD_SYNCED]: 'Grow Custom Field Synced',
  [AuditAction.GROW_CUSTOM_FIELDS_CACHE_REFRESHED]: 'Grow Custom Fields Cache Refreshed',

  // Grow Token Events
  [AuditAction.GROW_TOKEN_REFRESHED]: 'Grow Token Refreshed',
  [AuditAction.GROW_TOKEN_REFRESH_FAILED]: 'Grow Token Refresh Failed',

  // Dispute Events
  [AuditAction.DISPUTE_CREATED]: 'Dispute Created',
  [AuditAction.DISPUTE_WON]: 'Dispute Won',
  [AuditAction.DISPUTE_LOST]: 'Dispute Lost',

  // Webhook Events
  [AuditAction.WEBHOOK_CREATED]: 'Webhook Created',
  [AuditAction.WEBHOOK_UPDATED]: 'Webhook Updated',
  [AuditAction.WEBHOOK_DELETED]: 'Webhook Deleted',
  [AuditAction.WEBHOOK_ENABLED]: 'Webhook Enabled',
  [AuditAction.WEBHOOK_DISABLED]: 'Webhook Disabled',
  [AuditAction.WEBHOOK_DISABLED_AUTO]: 'Webhook Disabled Automatically',
  [AuditAction.WEBHOOK_SECRET_ROTATED]: 'Webhook Secret Rotated',
  [AuditAction.INTEGRATION_WEBHOOK_DELIVERY_SUCCEEDED]: 'Webhook Delivery Succeeded',
  [AuditAction.INTEGRATION_WEBHOOK_DELIVERY_FAILED]: 'Webhook Delivery Failed',

  // Zapier Connection Events
  [AuditAction.ZAPIER_LOCATION_CONNECTED]: 'Zapier Location Connected',
  [AuditAction.ZAPIER_LOCATION_DISCONNECTED]: 'Zapier Location Disconnected',
  [AuditAction.ZAPIER_SUBSCRIPTION_CREATED]: 'Zapier Subscription Created',
  [AuditAction.ZAPIER_SUBSCRIPTION_REMOVED]: 'Zapier Subscription Removed',

  // Content Suite Events
  [AuditAction.CONTENT_VIEWED]: 'Content Viewed',
  [AuditAction.CONTENT_COMPLETED]: 'Content Completed',
  [AuditAction.CONTENT_FAVORITED]: 'Content Favorited',
  [AuditAction.CONTENT_UNFAVORITED]: 'Content Unfavorited',
  [AuditAction.VIDEO_SESSION_STARTED]: 'Video Session Started',
  [AuditAction.VIDEO_COMPLETED]: 'Video Completed',
  [AuditAction.CHALLENGE_ENROLLED]: 'Challenge Enrolled',
  [AuditAction.CHALLENGE_WITHDRAWN]: 'Challenge Withdrawn',
  [AuditAction.CHALLENGE_COMPLETED]: 'Challenge Completed',
  [AuditAction.CHALLENGE_EXPIRED]: 'Challenge Expired',
  [AuditAction.CHALLENGE_GOAL_SUBMITTED]: 'Challenge Goal Submitted',
  [AuditAction.CHALLENGE_ASSESSMENT_SUBMITTED]: 'Challenge Assessment Submitted',
  [AuditAction.METRIC_ENTRY_CREATED]: 'Metric Entry Created',
  [AuditAction.METRIC_ENTRY_DELETED]: 'Metric Entry Deleted',
  [AuditAction.METRIC_PERSONAL_BEST]: 'Metric Personal Best',
  [AuditAction.AWARD_EARNED]: 'Award Earned',
  [AuditAction.STREAK_STARTED]: 'Streak Started',
  [AuditAction.STREAK_BROKEN]: 'Streak Broken',
  [AuditAction.MILESTONE_REACHED]: 'Milestone Reached',
  [AuditAction.LEADERBOARD_MATERIALIZED]: 'Leaderboard Materialized',
  [AuditAction.LEADERBOARD_RANK_CHANGED]: 'Leaderboard Rank Changed',

  // Content Suite — Carousel Group Events
  [AuditAction.CAROUSEL_GROUP_CREATED]: 'Carousel Group Created',
  [AuditAction.CAROUSEL_GROUP_UPDATED]: 'Carousel Group Updated',
  [AuditAction.CAROUSEL_GROUP_DELETED]: 'Carousel Group Deleted',

  // Content Suite — Offline Download Events
  [AuditAction.CONTENT_DOWNLOADED]: 'Content Downloaded',
  [AuditAction.CONTENT_DOWNLOAD_BATCH_SYNCED]: 'Content Download Batch Synced',
  [AuditAction.CONTENT_DOWNLOAD_ACCESS_CHECK]: 'Content Download Access Check',
  [AuditAction.CONTENT_WATCH_LATER_ADDED]: 'Watch Later Added',
  [AuditAction.CONTENT_WATCH_LATER_REMOVED]: 'Watch Later Removed',

  // Demo Events
  [AuditAction.DEMO_SIGNUP]: 'Demo Signup',
  [AuditAction.DEMO_EMAIL_VERIFIED]: 'Demo Email Verified',
  [AuditAction.DEMO_CORPORATE_CREATED]: 'Demo Corporate Created',
  [AuditAction.DEMO_SEEDING_STARTED]: 'Demo Seeding Started',
  [AuditAction.DEMO_SEEDING_COMPLETED]: 'Demo Seeding Completed',
  [AuditAction.DEMO_SEEDING_FAILED]: 'Demo Seeding Failed',
  [AuditAction.DEMO_READY]: 'Demo Ready',
  [AuditAction.DEMO_CLEANUP]: 'Demo Cleanup',

  // Goal Events
  [AuditAction.GOAL_CREATED]: 'Goal Created',
  [AuditAction.GOAL_UPDATED]: 'Goal Updated',
  [AuditAction.GOAL_DELETED]: 'Goal Deleted',

  // Share Events
  [AuditAction.SHARE_GENERATED]: 'Share Generated',
  [AuditAction.SHARE_COMPLETED]: 'Share Completed',

  // Referral Events
  [AuditAction.REFERRAL_RECORDED]: 'Referral Recorded',
  [AuditAction.REFERRAL_BONUS_AWARDED]: 'Referral Bonus Awarded',
  [AuditAction.REFERRAL_DECLINED]: 'Referral Declined',

  // Trial Events
  [AuditAction.TRIAL_ACCESS_PROVISIONED]: 'Trial Access Provisioned',
  [AuditAction.TRIAL_STARTED]: 'Trial Started',
  [AuditAction.TRIAL_EXTENDED]: 'Trial Extended',
  [AuditAction.TRIAL_CONVERTED]: 'Trial Converted',
  [AuditAction.TRIAL_EXPIRED]: 'Trial Expired',
  [AuditAction.TRIAL_CLOSED]: 'Trial Closed',
  [AuditAction.TRIAL_REOPENED]: 'Trial Reopened',

  // Staff Invitation Events
  [AuditAction.STAFF_INVITATION_SENT]: 'Staff Invitation Sent',
  [AuditAction.STAFF_INVITATION_REVOKED]: 'Staff Invitation Revoked',
  [AuditAction.STAFF_INVITATION_RESENT]: 'Staff Invitation Resent',
  [AuditAction.STAFF_INVITATION_ACCEPTED]: 'Staff Invitation Accepted',

  // Onboarding Events
  [AuditAction.ONBOARDING_STEP_COMPLETED]: 'Onboarding Step Completed',
  [AuditAction.ONBOARDING_STEP_VIEWED]: 'Onboarding Step Viewed',

  // Authentication Events
  [AuditAction.PASSWORD_RESET_REQUESTED]: 'Password Reset Requested',
  [AuditAction.MAGIC_LINK_SENT]: 'Magic Link Sent',

  // Member Agreement (e-signing) Events
  [AuditAction.AGREEMENT_SENT]: 'Agreement Sent',
  [AuditAction.AGREEMENT_VIEWED]: 'Agreement Viewed',
  [AuditAction.AGREEMENT_SIGNED]: 'Agreement Signed',
  [AuditAction.AGREEMENT_VOIDED]: 'Agreement Voided',

  // Bulk Import Events
  [AuditAction.MEMBER_CREATED_BULK]: 'Members Created (Bulk Import)',
  [AuditAction.MEMBER_LINKED_TO_SITE_BULK]: 'Members Linked to Site (Bulk Import)',
  [AuditAction.INVOICES_IMPORTED]: 'Invoices Imported (Bulk Import)',
}

/** Map resource types to human-readable labels */
export const RESOURCE_LABELS: Record<AuditResource, string> = {
  [AuditResource.MEMBER]: 'Member',
  [AuditResource.SESSION]: 'Session',
  [AuditResource.BOOKING]: 'Booking',
  [AuditResource.PAYMENT]: 'Payment',
  [AuditResource.ACCOUNT_CODE]: 'Ezypay Account Code', // CORE-1322 / INT-67 (neutral enum, branded label; DD-011)
  [AuditResource.INVOICE]: 'Invoice',
  [AuditResource.MEMBERSHIP]: 'Membership',
  [AuditResource.STAFF]: 'Staff',
  [AuditResource.ADMIN_USER]: 'Admin User',
  [AuditResource.CORPORATE]: 'Corporate',
  [AuditResource.SITE]: 'Site',
  [AuditResource.DOOR]: 'Door',
  [AuditResource.ACCESS_POINT]: 'Access Point',
  [AuditResource.ACCESS_ZONE]: 'Access Zone',
  [AuditResource.SYSTEM_CONFIG]: 'System Config',
  [AuditResource.PAGE]: 'Page',
  [AuditResource.REPORT]: 'Report',
  [AuditResource.SETTING]: 'Setting',
  // Knowledge Hub Resources
  [AuditResource.KNOWLEDGE_ARTICLE]: 'Knowledge Article',
  [AuditResource.KNOWLEDGE_CATEGORY]: 'Knowledge Category',
  [AuditResource.KNOWLEDGE_TRANSLATION]: 'Knowledge Translation',
  // AI Assistant Resources
  [AuditResource.AI_CONVERSATION]: 'AI Conversation',
  [AuditResource.AI_INTERACTION]: 'AI Interaction',
  [AuditResource.SUPPORT_TICKET]: 'Support Ticket',
  // Integration Resources
  [AuditResource.INTEGRATION_SYNC]: 'Integration Sync',
  [AuditResource.INTEGRATION_CLIENT]: 'Integration Client',
  [AuditResource.INTEGRATION_LOCATION]: 'Integration Location',
  [AuditResource.INTEGRATION_TOKEN]: 'Integration Token',
  // Grow Resources
  [AuditResource.GROW_CORPORATE]: 'Grow',
  [AuditResource.GROW_HQ]: 'Grow HQ',
  [AuditResource.GROW_SITE]: 'Grow Site',
  [AuditResource.GROW_TOKEN]: 'Grow Token',
  // Zapier Resources
  [AuditResource.ZAPIER_CONNECTION]: 'Zapier Connection',
  [AuditResource.ZAPIER_SUBSCRIPTION]: 'Zapier Subscription',

  // Webhook Resources
  [AuditResource.WEBHOOK]: 'Webhook',
  [AuditResource.INTEGRATION_WEBHOOK_DELIVERY]: 'Webhook Delivery',

  // Dispute Resources
  [AuditResource.DISPUTE]: 'Dispute',

  // Content Suite Resources
  [AuditResource.CONTENT_ENTRY]: 'Content Entry',
  [AuditResource.CONTENT_VIDEO]: 'Content Video',
  [AuditResource.CONTENT_CHALLENGE]: 'Content Challenge',
  [AuditResource.CONTENT_METRIC]: 'Content Metric',
  [AuditResource.CONTENT_AWARD]: 'Content Award',
  [AuditResource.CONTENT_LEADERBOARD]: 'Content Leaderboard',
  [AuditResource.CONTENT_CAROUSEL_GROUP]: 'Carousel Group',
  [AuditResource.CONTENT_GOAL]: 'Content Goal',

  // Share Resources
  [AuditResource.SHARE_CARD]: 'Share Card',

  // Additional
  [AuditResource.COMMUNICATION]: 'Communication',
  [AuditResource.DEMO_PROFILE]: 'Demo Profile',
  [AuditResource.IMPORT_SESSION]: 'Import Session',

  // Trial Resources
  [AuditResource.TRIAL]: 'Trial',

  // Invitation Resources
  [AuditResource.STAFF_INVITATION]: 'Staff Invitation',

  // Onboarding Resources
  [AuditResource.ONBOARDING_CHECKLIST]: 'Onboarding Checklist',

  // Agreement Resources
  [AuditResource.AGREEMENT]: 'Agreement',
}

/** Map visibility levels to colors and labels */
export const VISIBILITY_CONFIG: Record<AuditVisibility, { label: string; color: string }> = {
  [AuditVisibility.CUSTOMER]: { label: 'Customer', color: 'success' },
  [AuditVisibility.INTERNAL]: { label: 'Internal', color: 'info' },
  [AuditVisibility.SYSTEM]: { label: 'System', color: 'default' },
}

/** Map severity levels to colors and labels */
export const SEVERITY_CONFIG: Record<AuditSeverity, { label: string; color: string }> = {
  [AuditSeverity.INFO]: { label: 'Info', color: 'info' },
  [AuditSeverity.WARNING]: { label: 'Warning', color: 'warning' },
  [AuditSeverity.CRITICAL]: { label: 'Critical', color: 'error' },
  [AuditSeverity.SECURITY]: { label: 'Security', color: 'secondary' },
}

const ACCESS_OUTCOME_ACTIONS: ReadonlySet<AuditAction> = new Set([
  AuditAction.MEMBER_CHECKIN,
  AuditAction.DOOR_ACCESS_GRANTED,
  AuditAction.DOOR_ACCESS_DENIED,
  AuditAction.DOOR_UNLOCKED,
  AuditAction.DOOR_UNLOCK_FAILED,
  AuditAction.DOOR_LOCKED,
])

function getAccessOutcome(
  action: AuditAction,
  severity: AuditSeverity
): 'success' | 'failure' | null {
  if (!ACCESS_OUTCOME_ACTIONS.has(action)) return null
  return severity === AuditSeverity.INFO ? 'success' : 'failure'
}

export function getAccessOutcomeStyle<T>(
  action: AuditAction,
  severity: AuditSeverity,
  styleMap: { success: T; failure: T; default: T }
): T {
  const outcome = getAccessOutcome(action, severity)
  return outcome ? styleMap[outcome] : styleMap.default
}

/** Map source to human-readable labels */
export const SOURCE_LABELS: Record<AuditSource, string> = {
  [AuditSource.CORE_WEB]: 'Core Web',
  [AuditSource.ADMIN_WEB]: 'Admin Web',
  [AuditSource.EMBED_WEB]: 'Embed',
  [AuditSource.MOBILE]: 'Mobile App',
  [AuditSource.KIOSK]: 'Kiosk',
  [AuditSource.API]: 'API',
  [AuditSource.SYSTEM]: 'System',
  [AuditSource.WEBHOOK]: 'Webhook',
  [AuditSource.INTEGRATION]: 'Integration',
}

/** Action category groups for filter UI */
export const ACTION_CATEGORIES = {
  member: {
    label: 'Member',
    actions: [
      AuditAction.MEMBER_CREATED,
      AuditAction.MEMBER_UPDATED,
      AuditAction.MEMBER_DELETED,
      AuditAction.MEMBER_MERGED,
      AuditAction.MEMBER_LOGIN,
      AuditAction.MEMBER_LOGOUT,
      AuditAction.MEMBER_CHECKIN,
      AuditAction.MEMBER_CHECKOUT,
      AuditAction.MEMBER_PROFILE_VIEWED,
    ],
  },
  session: {
    label: 'Session/Booking',
    actions: [
      AuditAction.SESSION_BOOKED,
      AuditAction.SESSION_CANCELLED,
      AuditAction.SESSION_ATTENDED,
      AuditAction.SESSION_NO_SHOW,
      AuditAction.SESSION_WAITLISTED,
      AuditAction.SESSION_CREATED,
      AuditAction.SESSION_UPDATED,
      AuditAction.SESSION_DELETED,
    ],
  },
  payment: {
    label: 'Payment',
    actions: [
      AuditAction.PAYMENT_PROCESSED,
      AuditAction.PAYMENT_FAILED,
      AuditAction.PAYMENT_REFUNDED,
      AuditAction.INVOICE_CREATED,
      AuditAction.INVOICE_UPDATED,
      AuditAction.INVOICE_PAID,
      AuditAction.INVOICE_VOIDED,
    ],
  },
  membership: {
    label: 'Membership',
    actions: [
      AuditAction.MEMBERSHIP_CREATED,
      AuditAction.MEMBERSHIP_UPDATED,
      AuditAction.MEMBERSHIP_CANCELLED,
      AuditAction.MEMBERSHIP_SUSPENDED,
      AuditAction.MEMBERSHIP_RESUMED,
      AuditAction.MEMBERSHIP_RENEWED,
    ],
  },
  staff: {
    label: 'Staff',
    actions: [
      AuditAction.STAFF_LOGIN,
      AuditAction.STAFF_LOGOUT,
      AuditAction.STAFF_CREATED,
      AuditAction.STAFF_UPDATED,
      AuditAction.STAFF_DELETED,
      AuditAction.STAFF_ROLE_CHANGED,
    ],
  },
  admin: {
    label: 'Admin',
    actions: [
      AuditAction.ADMIN_USER_LOGIN,
      AuditAction.ADMIN_USER_LOGOUT,
      AuditAction.ADMIN_USER_CREATED,
      AuditAction.ADMIN_USER_UPDATED,
      AuditAction.ADMIN_USER_DELETED,
      AuditAction.IMPERSONATION_STARTED,
      AuditAction.IMPERSONATION_ENDED,
    ],
  },
  organization: {
    label: 'Organization',
    actions: [
      AuditAction.CORPORATE_CREATED,
      AuditAction.CORPORATE_UPDATED,
      AuditAction.SITE_CREATED,
      AuditAction.SITE_UPDATED,
      AuditAction.SITE_DELETED,
    ],
  },
  access: {
    label: 'Door/Access',
    actions: [
      AuditAction.DOOR_ACCESS_GRANTED,
      AuditAction.DOOR_ACCESS_DENIED,
      AuditAction.DOOR_UNLOCKED,
      AuditAction.DOOR_UNLOCK_FAILED,
      AuditAction.DOOR_LOCKED,
      AuditAction.ACCESS_POINT_MOVED,
      AuditAction.ACCESS_POINT_DELETED,
      AuditAction.ACCESS_POINT_CREATED,
      AuditAction.ACCESS_POINT_UPDATED,
      AuditAction.ACCESS_POINT_QR_GENERATED,
      AuditAction.ACCESS_ZONE_CREATED,
      AuditAction.ACCESS_ZONE_UPDATED,
      AuditAction.ACCESS_ZONE_DELETED,
    ],
  },
  system: {
    label: 'System',
    actions: [
      AuditAction.SYSTEM_CONFIG_CHANGED,
      AuditAction.DATA_EXPORT,
      AuditAction.DATA_IMPORT,
      AuditAction.GDPR_REQUEST,
      AuditAction.DATA_DELETION,
      AuditAction.PAGE_VIEWED,
      AuditAction.FEATURE_ACCESSED,
    ],
  },
  integrations: {
    label: 'Integrations',
    actions: [
      // Generic Integration Sync
      AuditAction.INTEGRATION_SYNC_STARTED,
      AuditAction.INTEGRATION_SYNC_COMPLETED,
      AuditAction.INTEGRATION_SYNC_FAILED,
      AuditAction.INTEGRATION_CLIENT_SYNCED,
      AuditAction.INTEGRATION_CLIENT_SYNC_FAILED,
      AuditAction.INTEGRATION_TOKEN_REFRESHED,
      AuditAction.INTEGRATION_TOKEN_REFRESH_FAILED,
      // Grow Corporate Settings
      AuditAction.GROW_CORPORATE_SETTINGS_UPDATED,
      // Grow HQ
      AuditAction.GROW_HQ_CREATED,
      AuditAction.GROW_HQ_UPDATED,
      AuditAction.GROW_HQ_DELETED,
      AuditAction.GROW_HQ_AUTHENTICATION_SET,
      AuditAction.GROW_HQ_SYNC_TRIGGERED,
      // Grow Site
      AuditAction.GROW_SITE_AUTHENTICATION_SET,
      AuditAction.GROW_SITE_LINKED_TO_HQ,
      AuditAction.GROW_SITE_LOCATION_CREATED,
      AuditAction.GROW_SITE_SYNC_TRIGGERED,
      // Grow Custom Fields
      AuditAction.GROW_CUSTOM_FIELD_SYNCED,
      AuditAction.GROW_CUSTOM_FIELDS_CACHE_REFRESHED,
      // Grow Token
      AuditAction.GROW_TOKEN_REFRESHED,
      AuditAction.GROW_TOKEN_REFRESH_FAILED,
    ],
  },
}

/**
 * Simple string-keyed resource labels for lowercase lookups
 * Use these when working with raw API responses where resource is lowercase
 */
export const RESOURCE_LABELS_SIMPLE: Record<string, string> = {
  member: 'Member',
  session: 'Session',
  booking: 'Booking',
  payment: 'Payment',
  invoice: 'Invoice',
  membership: 'Membership',
  staff: 'Staff',
  admin_user: 'Admin User',
  corporate: 'Corporate',
  site: 'Site',
  door: 'Door',
  access_point: 'Access Point',
  system_config: 'System Config',
  page: 'Page',
  report: 'Report',
  class_session: 'Class Session',
  schedule: 'Schedule',
  user: 'User',
  product: 'Product',
  api_key: 'API Key',
  webhook: 'Webhook',
  integration: 'Integration',
  notification: 'Notification',
  campaign: 'Campaign',
  setting: 'Setting',
  settings: 'Settings',
  // Integration resources
  integration_sync: 'Integration Sync',
  integration_client: 'Integration Client',
  integration_location: 'Integration Location',
  integration_token: 'Integration Token',
  // Grow resources
  grow_corporate: 'Grow',
  grow_hq: 'Grow HQ',
  grow_site: 'Grow Site',
  grow_token: 'Grow Token',
}

/**
 * Simple string-keyed resource colors for lowercase lookups
 * Use these when working with raw API responses where resource is lowercase
 */
export const RESOURCE_COLORS_SIMPLE: Record<
  string,
  'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'
> = {
  member: 'success',
  session: 'info',
  booking: 'info',
  payment: 'success',
  invoice: 'warning',
  membership: 'success',
  staff: 'primary',
  admin_user: 'secondary',
  corporate: 'secondary',
  site: 'secondary',
  door: 'warning',
  access_point: 'warning',
  system_config: 'warning',
  page: 'info',
  report: 'info',
  class_session: 'info',
  schedule: 'info',
  user: 'success',
  product: 'secondary',
  api_key: 'error',
  webhook: 'secondary',
  integration: 'secondary',
  notification: 'info',
  campaign: 'info',
  setting: 'info',
  settings: 'secondary',
  // Integration resources
  integration_sync: 'info',
  integration_client: 'success',
  integration_location: 'secondary',
  integration_token: 'warning',
  // Grow resources
  grow_hq: 'primary',
  grow_site: 'primary',
  grow_token: 'warning',
}
